import React from 'react';
import { alpha, ThemeOptions } from '@mui/material/styles';
import type { PaletteOptions } from '@mui/material/styles';
import { DEFAULT_ELEVATION } from 'shared/lib/utils/uiConstants';

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    buttonsTonalInverse: true;
  }
}

declare module '@mui/material/styles' {
  interface PaletteColor {
    tonal?: string;
    tonalHover?: string;
  }

  interface SimplePaletteColorOptions {
    tonal?: string;
    tonalHover?: string;
  }

  interface Palette {
    neutral: PaletteColor;
    buttonsTonalInverse: Palette['primary'];
    custom: {
      previewImageBg: string;
      primaryMainHighlight: string;
      primaryMainOpacity01: string;
      separatorLine: string;
      customDark: string;
      borders: string;
      isFavourite: string;
      isFavouriteDark: string;
      textInactive: string;
      adminRequest: string;
      info: string;
      roles: {
        ADMIN: string;
        EDITOR: string;
        VIEWER: string;
      };
      lightGrey: string;
      tableOdd: string;
      selected: string;
      successGreen: string;
      grey: string;
    };
    customBackgrounds: {
      elevation4: string;
      elevation5: string;
      elevation6: string;
      elevation10: string;
      hoverEffect: string;
      contrast: string;
    };
  }

  interface PaletteOptions {
    neutral?: SimplePaletteColorOptions;
    buttonsTonalInverse?: PaletteOptions['primary'];
    custom?: {
      primaryMainHighlight: string;
      previewImageBg: string;
      primaryMainOpacity01: string;
      separatorLine: string;
      customDark: string;
      borders: string;
      isFavourite: string;
      isFavouriteDark: string;
      textInactive: string;
      adminRequest: string;
      info: string;
      roles: {
        ADMIN: string;
        EDITOR: string;
        VIEWER: string;
      };
      lightGrey: string;
      tableOdd: string;
      selected: string;
      successGreen: string;
      grey: string;
    };
    customBackgrounds?: {
      elevation4: string;
      elevation5: string;
      elevation6: string;
      elevation10: string;
      hoverEffect: string;
      contrast: string;
    };
  }

  interface BreakpointOverrides {
    xs: false; // removes the `xs` breakpoint
    sm: false;
    md: false;
    lg: false;
    xl: false;
    mobile: true; // adds the `mobile` breakpoint
    tablet: true;
    laptop: true;
    desktop_small: true;
    desktop_middle: true;
    desktop_large: true;
  }

  // Типография
  interface TypographyVariants {
    f8: React.CSSProperties;
    f8b: React.CSSProperties;
    f10: React.CSSProperties;
    f10b: React.CSSProperties;
    f12: React.CSSProperties;
    f12b: React.CSSProperties;
    f12bfixed: React.CSSProperties;
    f12fixed: React.CSSProperties;
    f14: React.CSSProperties;
    f14b: React.CSSProperties;
    f16b: React.CSSProperties;
    f16bfixed: React.CSSProperties;
    f16fixed: React.CSSProperties;
    f16: React.CSSProperties;
    f18: React.CSSProperties;
    f18b: React.CSSProperties;
    f19: React.CSSProperties;
    f20: React.CSSProperties;
    f22: React.CSSProperties;
    f20b: React.CSSProperties;
    f24: React.CSSProperties;
    f24b: React.CSSProperties;
    f24bfixed: React.CSSProperties;
    f28: React.CSSProperties;
    f30: React.CSSProperties;
    f36b: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    f8: React.CSSProperties;
    f8b: React.CSSProperties;
    f10: React.CSSProperties;
    f10b: React.CSSProperties;
    f12: React.CSSProperties;
    f12b: React.CSSProperties;
    f12fixed: React.CSSProperties;
    f12bfixed: React.CSSProperties;
    f14: React.CSSProperties;
    f14b: React.CSSProperties;
    f16b: React.CSSProperties;
    f16bfixed: React.CSSProperties;
    f16fixed: React.CSSProperties;
    f16: React.CSSProperties;
    f18: React.CSSProperties;
    f18b: React.CSSProperties;
    f19: React.CSSProperties;
    f20: React.CSSProperties;
    f22: React.CSSProperties;
    f20b: React.CSSProperties;
    f24: React.CSSProperties;
    f24b: React.CSSProperties;
    f24bfixed: React.CSSProperties;
    f28: React.CSSProperties;
    f30: React.CSSProperties;
    f36b: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    f8: true;
    f8b: true;
    f10: true;
    f10b: true;
    f12: true;
    f12b: true;
    f12fixed: true;
    f12bfixed: true;
    f14: true;
    f14b: true;
    f16b: true;
    f16: true;
    f16bfixed: true;
    f16fixed: true;
    f18: true;
    f18b: true;
    f19: true;
    f20: true;
    f22: true;
    f20b: true;
    f24: true;
    f24b: true;
    f24bfixed: true;
    f28: true;
    f30: true;
    f36b: true;
  }
}

export const defaultPalette: PaletteOptions = {
  success: {
    main: '#226F54',
  },
  warning: {
    main: '#FBB02D',
  },
  error: {
    main: '#E90808',
  },
} as const;

const baseNeutral = '#070707';

export const lightThemeParams: ThemeOptions = {
  palette: {
    buttonsTonalInverse: {
      main: '#7C28E3',
      light: '#9A46FF',
      dark: '#9A46FF',
      contrastText: 'white',
    },
    primary: {
      main: '#7C28E3',
      contrastText: 'white',
      tonalHover: '#F2E8FD',
    },
    secondary: {
      main: '#fff',
      contrastText: '#9747FF',
    },
    neutral: {
      main: baseNeutral,
      tonal: alpha(baseNeutral, 0.1),
      tonalHover: alpha(baseNeutral, 0.05),
    },
    text: {
      primary: '#333333',
      secondary: '#8290A1',
    },
    background: {
      default: 'white',
      paper: 'white',
    },
    customBackgrounds: {
      elevation4: '#F8F8F8',
      elevation5: 'white',
      elevation6: '#F2F2F2',
      elevation10: '#ffffff',
      contrast: 'black',
      hoverEffect: '#EBEBEB',
    },
    custom: {
      primaryMainHighlight: '#6931B2',
      previewImageBg: '#b3b3b3',
      primaryMainOpacity01: 'rgba(151, 71, 255, 0.1)',
      customDark: '#7E7E7E',
      separatorLine: '#BFC8CA',
      borders: '#E6E9EC',
      isFavourite: '#7C28E3',
      isFavouriteDark: '#e4cf10',
      textInactive: '#616879',
      adminRequest: '#185ABD',
      info: '#3A86FF',
      roles: {
        ADMIN: '#333333',
        EDITOR: '#107C41',
        VIEWER: '#7C28E3',
      },
      lightGrey: '#DBE1E6',
      tableOdd: '#EEEEEE',
      selected: '#E9D6FF',
      successGreen: '#66C95D',
      grey: '#07070794',
    },
    ...defaultPalette,
  },
};

export const darkThemeParams: ThemeOptions = {
  palette: {
    primary: {
      main: '#7C28E3',
      contrastText: 'white',
    },
    secondary: {
      main: '#3A86FF',
    },
    background: {
      default: '#1D2633',
      paper: '#1D2633',
    },
    text: {
      secondary: '#617CA6',
    },
    customBackgrounds: {
      elevation4: '#161C26', // Ниже основного - темнее
      elevation5: '#1D2633', // Считается как основной уровень, должен совпадать с background.paper, так же см components ниже
      elevation6: '#303E54', // Выше - светлее
      elevation10: '#ffffff', // Выше - светлее
      hoverEffect: '#EBEBEB',
      contrast: 'white', // Цвет контрастный всем основным фонам
    },
    ...defaultPalette,
  },
};

export const commonThemeParams: ThemeOptions = {
  breakpoints: {
    values: {
      mobile: 0,
      tablet: 640,
      laptop: 940,
      desktop_small: 1280,
      desktop_middle: 1440,
      desktop_large: 1920,
    },
  },
  spacing: 4,
  shape: {
    borderRadius: 20,
  },
  typography: {
    fontFamily: 'MoscowSans',
    button: {
      fontSize: '0.75rem',
      lineHeight: 'normal',
    },
    f8: {
      fontSize: '0.5rem',
      lineHeight: 'normal',
    },
    f8b: {
      fontSize: '0.5rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f10: {
      fontSize: '0.625rem',
      lineHeight: 'normal',
    },
    f10b: {
      fontSize: '0.625rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f12: {
      fontSize: '0.75rem',
      lineHeight: 'normal',
    },
    f12fixed: {
      fontSize: '0.75rem',
      lineHeight: '0.875rem',
    },
    f12b: {
      fontSize: '0.75rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f12bfixed: {
      fontSize: '0.75rem',
      fontWeight: 'bold',
      lineHeight: '0.875rem',
    },
    f14: {
      fontSize: '0.875rem',
      lineHeight: 'normal',
    },
    f14b: {
      fontSize: '0.875rem',
      fontWeight: 'bold',
    },
    f16: {
      fontSize: '1rem',
      lineHeight: 'normal',
    },
    f16b: {
      fontSize: '1rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f16fixed: {
      fontSize: '1rem',
      lineHeight: '1.125rem',
    },
    f16bfixed: {
      fontSize: '1rem',
      fontWeight: 'bold',
      lineHeight: '1.125rem',
    },
    f18: {
      fontSize: '1.125rem',
      lineHeight: 'normal',
    },
    f18b: {
      fontSize: '1.125rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f19: {
      fontSize: '1.1875rem',
      lineHeight: 'normal',
    },
    f20: {
      fontSize: '1.25rem',
      lineHeight: 'normal',
    },
    f22: {
      fontSize: '1.375rem',
      lineHeight: 'normal',
    },
    f20b: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f24: {
      fontSize: '1.5rem',
      lineHeight: 'normal',
    },
    f24b: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f24bfixed: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      lineHeight: '1.8125rem',
    },
    f28: {
      fontSize: '1.75rem',
      lineHeight: 'normal',
    },
    f30: {
      fontSize: '1.875rem',
      lineHeight: 'normal',
    },
    f36b: {
      fontSize: '2.375rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
  },
  components: {
    MuiPaper: {
      // За основу берем значение 5 чтобы был запас для маневров
      defaultProps: {
        elevation: DEFAULT_ELEVATION,
      },
      styleOverrides: {
        // Вырубаем стандартное поведение, чтобы определить точно в соответствии с макетом
        elevation: {
          boxShadow: 'none',
          backgroundImage: 'none',
        },
        elevation6: ({ theme }) => {
          return {
            backgroundColor: theme.palette.customBackgrounds.elevation6,
          };
        },
        elevation5: ({ theme }) => {
          return {
            backgroundColor: theme.palette.customBackgrounds.elevation5,
          };
        },
        elevation4: ({ theme }) => {
          return {
            backgroundColor: theme.palette.customBackgrounds.elevation4,
          };
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: DEFAULT_ELEVATION,
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          whiteSpace: 'nowrap',
          boxShadow: 'none !important',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#DBE1E6',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableFocusRipple: true },
      styleOverrides: {
        root: ({ theme }) => ({
          'textTransform': 'none',
          '& .MuiButton-startIcon': {
            marginLeft: 0,
            marginRight: theme.spacing(1),
          },
        }),
        sizeLarge: ({ theme }) => ({
          ...theme.typography.f20,
          borderRadius: 100,
          paddingTop: theme.spacing(3),
          paddingBottom: theme.spacing(3),
        }),
        sizeMedium: ({ theme }) => {
          return {
            ...theme.typography.f16,
            borderRadius: 100,
            height: '42px !important',
            lineHeight: 0,
          };
        },
        sizeSmall: ({ theme }) => ({
          ...theme.typography.f14,
          borderRadius: 100,
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(2),
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            gap: `${theme.spacing(1)}`,
          };
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: '0px 4px 8px 0px #0000000F, 0px 0px 4px 0px #0000000A',
          borderRadius: '10px',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          transform: 'none',
        },
      },
    },
  },
};

export const defaultThemeParams: ThemeOptions = {
  ...lightThemeParams,
  ...commonThemeParams,
};
